import React from 'react';
import Switch from 'react-switch';
import { toast } from 'react-toastify';
import { ColorPicker, ColorIndicator } from '@wordpress/components';
import { notifyError } from '../../utils/toast';

const GeneralSettings = ({ onChange }) => {
  const [tooltip, setTooltip] = React.useState(tpwvs_admin_settings.tpwvs_general.tooltip);
  const [tooltipBackgroundColor, setTooltipBackgroundColor] = React.useState(tpwvs_admin_settings.tpwvs_general.tooltip_background);
  const [tooltipBackgroundModal, setTooltipBackgroundModal] = React.useState(false);
  const [tooltipFontColor, setTooltipFontColor] = React.useState(tpwvs_admin_settings.tpwvs_general.tooltip_font_color);
  const [tooltipFontModal, setTooltipFontModal] = React.useState(false);
  const [tooltipPosition, setTooltipPosition] = React.useState(tpwvs_admin_settings.tpwvs_general.tooltip_position);
  const [swatchStyle, setSwatchStyle] = React.useState(tpwvs_admin_settings.tpwvs_general.swatch_style);
  const [swatchSize, setSwatchSize] = React.useState(tpwvs_admin_settings.tpwvs_general.swatch_size);
  const [loading, setLoading] = React.useState(false);

  const notifyPreview = (updated) => {
    try {
      if (typeof onChange === 'function') onChange(updated);
      if (window && typeof window.tpwvsUpdateSettings === 'function') {
        window.tpwvsUpdateSettings(updated);
      } else {
        window.tpwvs_admin_settings = { ...(window.tpwvs_admin_settings || {}), ...updated };
        window.dispatchEvent(new CustomEvent('tpwvs_settings_updated', { detail: window.tpwvs_admin_settings }));
      }
    } catch (err) {
      // ignore
    }
  };

  const saveSettings = async (payload) => {
    const formData = new FormData();
    formData.append('action', 'tpwvs_update_settings');
    formData.append('security', tpwvs_admin_settings.update_nonce);
    if (payload.tpwvs_general) {
      formData.append('tpwvs_general', JSON.stringify(payload.tpwvs_general));
    }

    const resp = await fetch(tpwvs_admin_settings.api_url, { method: 'POST', body: formData });
    toast.promise(resp.json(), {
      pending: {
        render() { setLoading(true); return 'Data is loading'; }, icon: true
      },
      success: {
        render({ data }) { setLoading(false); notifyPreview(payload); return data.data.message; }, icon: true
      },
      error: {
        render() { return notifyError('Api loaded failed'); }
      }
    });
  };

  const handleTooltipToggle = (isChecked) => {
    const next = !tooltip;
    setTooltip(next);
    const updated = { tpwvs_general: { tooltip: next, tooltip_position: tooltipPosition, tooltip_background: tooltipBackgroundColor, tooltip_font_color: tooltipFontColor, swatch_style: swatchStyle, swatch_size: swatchSize } };
    saveSettings(updated);
  };

  const handleTooltipPosition = (e) => {
    const value = e.target.value;
    setTooltipPosition(value);
    const updated = { tpwvs_general: { tooltip, tooltip_position: value, tooltip_background: tooltipBackgroundColor, tooltip_font_color: tooltipFontColor, swatch_style: swatchStyle, swatch_size: swatchSize } };
    saveSettings(updated);
  };

  const handleStyle = (e) => {
    const value = e.target.value;
    setSwatchStyle(value);
    const updated = { tpwvs_general: { tooltip, tooltip_position: tooltipPosition, tooltip_background: tooltipBackgroundColor, tooltip_font_color: tooltipFontColor, swatch_style: value, swatch_size: swatchSize } };
    saveSettings(updated);
  };

  const handleTooltipBackground = () => {
    setTooltipBackgroundModal(false);
    const updated = { tpwvs_general: { tooltip, tooltip_position: tooltipPosition, tooltip_background: tooltipBackgroundColor, tooltip_font_color: tooltipFontColor, swatch_style: swatchStyle, swatch_size: swatchSize } };
    saveSettings(updated);
  };

  const handleTooltipFontColor = () => {
    setTooltipFontModal(false);
    const updated = { tpwvs_general: { tooltip, tooltip_position: tooltipPosition, tooltip_background: tooltipBackgroundColor, tooltip_font_color: tooltipFontColor, swatch_style: swatchStyle, swatch_size: swatchSize } };
    saveSettings(updated);
  };

  const handleSwatchSize = () => {
    const updated = { tpwvs_general: { tooltip, tooltip_position: tooltipPosition, tooltip_background: tooltipBackgroundColor, tooltip_font_color: tooltipFontColor, swatch_style: swatchStyle, swatch_size: swatchSize } };
    saveSettings(updated);
  };

  React.useEffect(() => {
    const current = {
      tpwvs_general: { tooltip, tooltip_position: tooltipPosition, tooltip_background: tooltipBackgroundColor, tooltip_font_color: tooltipFontColor, swatch_style: swatchStyle, swatch_size: swatchSize }
    };
    notifyPreview(current);
  }, [tooltip, tooltipBackgroundColor, tooltipFontColor, tooltipPosition, swatchStyle, swatchSize]);

  return (
    <div className="tpv-settings-general h-[600px] overflow-auto">
      <div className="tpv-settings-option sm:flex justify-between items-center pt-[26px] pb-[23px] px-9 border-[#F4F5F9] border-b-2 last:border-0">
        <div className="tpv-settings-option-content mb-4 sm:mb-0">
          <h4 className="text-lg mb-0">Enable Tooltip</h4>
          <p className="mb-0">Enable tooltip on the swatches for showing label.</p>
        </div>
        <div className="tpv-settings-option-switch">
          <div className="flex sm:justify-center items-center">
            <label>
              <Switch onChange={handleTooltipToggle} checked={tooltip} className={`react-switch ${tooltip ? 'active' : ''}`} uncheckedIcon={false} checkedIcon={false} id="material-switch" />
            </label>
          </div>
        </div>
      </div>

      <div className="tpv-settings-option sm:flex justify-between items-center pt-[26px] pb-[23px] px-9 border-[#F4F5F9] border-b-2 last:border-0">
        <div className="tpv-settings-option-content mb-4 sm:mb-0">
          <h4 className="text-lg mb-0">Tooltip Position</h4>
          <p className="mb-0">Turn the default type to button type.</p>
        </div>
        <div className="tpv-settings-option-select tp-tooltip-select">
          <select onChange={handleTooltipPosition} className={tooltipPosition} value={tooltipPosition}>
            <option value={"top"}>Top</option>
            <option value={"bottom"}>Bottom</option>
            <option value={"left"}>Left</option>
            <option value={"right"}>Right</option>
          </select>
        </div>
      </div>

      <div className="tpv-settings-option sm:flex justify-between items-center pt-[26px] pb-[23px] px-9 border-[#F4F5F9] border-b-2 last:border-0">
        <div className="tpv-settings-option-content mb-4 sm:mb-0">
          <h4 className="text-lg mb-0">Tooltip Background</h4>
          <p className="mb-3">Change the toolltip background color by selecting a dynamic color.</p>
          <button type="button" onClick={handleTooltipBackground} className="rounded bg-theme text-white px-3 py-0.5 hover:opacity-90 text-sm">Save</button>
        </div>
        <div className="tpv-settings-option-select tp-tooltip-select tp-toolltip-color">
          <div className="tp-color-indicator" onClick={() => setTooltipBackgroundModal(true)}>
            <div className="tp-indicator"><ColorIndicator colorValue={tooltipBackgroundColor} /></div>
            <p>{tooltipBackgroundColor}</p>
            {tooltipBackgroundModal && <div className="tp-color-picker"><ColorPicker color={tooltipBackgroundColor} onChange={(color) => setTooltipBackgroundColor(typeof color === 'string' ? color : color.hex)} defaultValue={tooltipBackgroundColor} /></div>}
          </div>
        </div>
      </div>

      <div className="tpv-settings-option sm:flex justify-between items-center pt-[26px] pb-[23px] px-9 border-[#F4F5F9] border-b-2 last:border-0">
        <div className="tpv-settings-option-content mb-4 sm:mb-0">
          <h4 className="text-lg mb-0">Tooltip Font Color</h4>
          <p className="mb-3">Change the toolltip font color by selecting a dynamic color.</p>
          <button type="button" onClick={handleTooltipFontColor} className="rounded bg-theme text-white px-3 py-0.5 hover:opacity-90 text-sm">Save</button>
        </div>
        <div className="tpv-settings-option-select tp-tooltip-select tp-toolltip-color">
          <div className="tp-color-indicator" onClick={() => setTooltipFontModal(true)}>
            <div className="tp-indicator"><ColorIndicator colorValue={tooltipFontColor} /></div>
            <p>{tooltipFontColor}</p>
            {tooltipFontModal && <div className="tp-color-picker"><ColorPicker color={tooltipFontColor} onChange={(color) => setTooltipFontColor(typeof color === 'string' ? color : color.hex)} defaultValue={tooltipFontColor} /></div>}
          </div>
        </div>
      </div>

      <div className="tpv-settings-option  sm:flex justify-between items-center pt-[26px] pb-[23px] px-9 border-[#F4F5F9] border-b-2 last:border-0">
        <div className="tpv-settings-option-content mb-4 sm:mb-0">
          <h4 className="text-lg mb-0">Swatches Style</h4>
          <p className="mb-0">Swatches shape like rounded, square or default</p>
        </div>
        <div className="tpv-settings-option-select tp-tooltip-select tp-style-select">
          <select onChange={handleStyle} className={swatchStyle} value={swatchStyle}>
            <option value={"tp-square"}>Square</option>
            <option value={"tp-rounded"}>Rounded</option>
          </select>
        </div>
      </div>

      <div className="tpv-settings-option md:flex justify-between items-center pt-[26px] pb-[23px] px-9 border-[#F4F5F9] border-b-2 last:border-0">
        <div className="tpv-settings-option-content mb-4 md:mb-0">
          <h4 className="text-lg mb-0">Swatch Size</h4>
          <p className="mb-3">Give a size in number it will be given as height and width (px). Default:
            <code className="bg-[#F7F9FA] text-[#303136] h-7 px-2 inline-block">26px</code>
          </p>
          <button type="button" onClick={handleSwatchSize} className="rounded bg-theme text-white px-3 py-0.5 hover:opacity-90 text-sm">Save</button>
        </div>
        <div className="tpv-settings-option-input">
          <input type="number" min={10} value={swatchSize} className="tpv-outline-0 shadow-none w-[200px] h-10 px-4 text-[#303136] border-2 border-[#EFEFEF] rounded-md focus:border-theme" onChange={(e) => setSwatchSize(e.target.value)} />
        </div>
      </div>
    </div>
  );
};

export default GeneralSettings;
